import { memo } from 'react';
import type { ReactElement } from 'react';
import { flexRender, type Row, type Header } from '@tanstack/react-table';
import { cn } from '@/core/lib/utils';

interface ListRowProps<T> {
  row: Row<T>;
  className?: string;
  editingRowId?: string | null;
}

function ListRowComponent<T>({ row, className }: ListRowProps<T>): ReactElement {
  return (
    <div
      className={cn(
        'group flex items-center px-4 py-3 border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer',
        className,
      )}
    >
      {row.getVisibleCells().map((cell) => {
        const cellClassName = (cell.column.columnDef.meta as { cellClassName?: string })
          ?.cellClassName;
        return (
          <div
            key={cell.id}
            className={cn('px-2', cellClassName ?? 'flex-1 min-w-0')}
            style={cellClassName ? undefined : { width: cell.column.getSize() }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </div>
        );
      })}
    </div>
  );
}

export const ListRow = memo(ListRowComponent, (prev, next) => {
  if (prev.row.id !== next.row.id) return false;
  if (prev.row.original !== next.row.original) return false;
  if (prev.editingRowId !== next.editingRowId) return false;
  // Always re-render the row being actively edited so controlled inputs stay in sync
  if (next.editingRowId !== null && next.editingRowId === (next.row.original as { id: string }).id)
    return false;
  return true;
}) as <T>(props: ListRowProps<T>) => ReactElement;

interface ListHeaderRowProps<T> {
  headers: Header<T, unknown>[];
}

function ListHeaderRowComponent<T>({ headers }: ListHeaderRowProps<T>): ReactElement {
  return (
    <div className="flex items-center px-4 py-2 border-b border-border bg-muted/30 sticky top-0 z-10">
      {headers.map((header) => {
        const cellClassName = (header.column.columnDef.meta as { cellClassName?: string })
          ?.cellClassName;
        return (
          <div
            key={header.id}
            className={cn(
              'px-2 text-xs font-medium text-muted-foreground uppercase tracking-wider',
              cellClassName ?? 'flex-1 min-w-0',
            )}
            style={cellClassName ? undefined : { width: header.getSize() }}
          >
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </div>
        );
      })}
    </div>
  );
}

export const ListHeaderRow = memo(ListHeaderRowComponent) as <T>(
  props: ListHeaderRowProps<T>,
) => ReactElement;
